import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/projects/:slug/members` — the project's member roster. Each member holds
 * one or more Hub roles (what they do in the project) and, separately, their
 * GitLab access to the repository, which Hub pushes to GitLab.
 */

type ProjectRole = "manager" | "developer" | "tester" | "maintainer" | "client";
type GitlabAccess = "guest" | "planner" | "reporter" | "developer" | "maintainer" | "owner";

const ALL_PROJECT_ROLES: ProjectRole[] = ["manager", "developer", "tester", "maintainer", "client"];

const PROJECT_ROLE_LABEL: Record<ProjectRole, string> = {
  manager: "Manager",
  developer: "Developer",
  tester: "Tester",
  maintainer: "Maintainer",
  client: "Client",
};

const ACCESS_OPTIONS: Array<{ value: GitlabAccess | ""; label: string }> = [
  { value: "", label: "No repo access" },
  { value: "guest", label: "Guest" },
  { value: "planner", label: "Planner" },
  { value: "reporter", label: "Reporter" },
  { value: "developer", label: "Developer" },
  { value: "maintainer", label: "Maintainer" },
  { value: "owner", label: "Owner" },
];

type MemberUser = { id: string; name: string | null; email: string | null };

type Member = {
  id: string;
  roles: ProjectRole[];
  gitlabAccess: GitlabAccess | null;
  createdAt: string;
  userId: string;
  user: MemberUser;
  /** An agent: membership, role and access are fixed by Hub. */
  fixed: boolean;
};

type MembersResponse = {
  members: Member[];
  canManage: boolean;
  availableUsers: MemberUser[];
};

function RoleChips({
  value,
  onToggle,
  disabled,
}: {
  value: ProjectRole[];
  onToggle?: (role: ProjectRole) => void;
  disabled?: boolean;
}) {
  if (!onToggle) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((r) => (
          <Badge key={r} variant={r === "manager" ? "default" : "secondary"}>
            {PROJECT_ROLE_LABEL[r]}
          </Badge>
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {ALL_PROJECT_ROLES.map((r) => {
        const on = value.includes(r);
        return (
          <button
            key={r}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(r)}
            className={`rounded-full border px-2 py-0.5 text-xs transition-colors disabled:opacity-50 ${
              on
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input text-muted-foreground hover:bg-accent"
            }`}
          >
            {PROJECT_ROLE_LABEL[r]}
          </button>
        );
      })}
    </div>
  );
}

const toggle = (roles: ProjectRole[], r: ProjectRole) =>
  roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];

export const Route = createFileRoute("/_app/projects/$slug/members")({
  loader: ({ params }) => api.get<MembersResponse>(`/api/projects/${params.slug}/members/manage`),
  component: ProjectMembersPage,
});

function ProjectMembersPage() {
  const { members, canManage, availableUsers } = Route.useLoaderData();
  const params = Route.useParams();
  const router = useRouter();

  const [addUserId, setAddUserId] = useState("");
  const [addRoles, setAddRoles] = useState<ProjectRole[]>(["developer"]);
  const [addAccess, setAddAccess] = useState<GitlabAccess | "">("developer");
  const [busy, setBusy] = useState<string | null>(null);

  async function run(key: string, title: string, fn: () => Promise<unknown>) {
    setBusy(key);
    try {
      await fn();
      await router.invalidate();
    } catch (err) {
      toast({
        title,
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  }

  const addMember = () =>
    run("add", "Couldn't add the member", async () => {
      await api.post(`/api/projects/${params.slug}/members`, {
        userId: addUserId,
        roles: addRoles,
        gitlabAccess: addAccess || null,
      });
      setAddUserId("");
      setAddRoles(["developer"]);
      setAddAccess("developer");
    });

  const patchMember = (member: Member, body: Record<string, unknown>) =>
    run(member.id, "Couldn't change the member", () =>
      api.patch(`/api/projects/${params.slug}/members/${member.id}`, body),
    );

  const removeMember = (member: Member) =>
    run(member.id, "Couldn't remove the member", () =>
      api.delete(`/api/projects/${params.slug}/members/${member.id}`),
    );

  const columns: Array<DataTableColumn<Member>> = [
    {
      key: "name",
      header: "Name",
      sortAccessor: (m) => m.user.name,
      cellClassName: "font-medium",
      cell: (m) => m.user.name,
    },
    {
      key: "email",
      header: "Email",
      sortAccessor: (m) => m.user.email,
      cellClassName: "text-muted-foreground text-sm",
      cell: (m) => m.user.email,
    },
    {
      key: "roles",
      header: "Roles",
      sortAccessor: (m) => m.roles.join(","),
      cell: (m) =>
        canManage && !m.fixed ? (
          <RoleChips
            value={m.roles}
            disabled={busy === m.id}
            onToggle={(r) => {
              const roles = toggle(m.roles, r);
              if (roles.length) patchMember(m, { roles });
            }}
          />
        ) : (
          <RoleChips value={m.roles} />
        ),
    },
    {
      key: "gitlab",
      header: "GitLab access",
      sortAccessor: (m) => m.gitlabAccess ?? "",
      cell: (m) =>
        canManage && !m.fixed ? (
          <Select
            aria-label="GitLab access"
            size="sm"
            value={m.gitlabAccess ?? ""}
            disabled={busy === m.id}
            onChange={(v) => patchMember(m, { gitlabAccess: v || null })}
            options={ACCESS_OPTIONS}
          />
        ) : (
          <span className="text-sm capitalize text-muted-foreground">
            {m.gitlabAccess ?? "none"}
          </span>
        ),
    },
    {
      key: "joined",
      header: "Joined",
      sortAccessor: (m) => m.createdAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (m) => <LocalDate value={m.createdAt} />,
    },
    {
      key: "actions",
      header: "",
      hidden: !canManage,
      headClassName: "w-[90px]",
      cell: (member) =>
        member.fixed ? (
          <span className="text-xs text-muted-foreground">Fixed</span>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="h-8 text-xs px-2"
            disabled={busy === member.id}
            onClick={() => removeMember(member)}
          >
            Remove
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">Members</h2>
        <span className="text-sm text-muted-foreground">
          {members.length} member{members.length !== 1 ? "s" : ""}
        </span>
      </div>

      {canManage && availableUsers.length > 0 && (
        <div className="rounded-md border bg-card p-5">
          <h3 className="font-heading text-sm font-semibold mb-3">Add Member</h3>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                User
              </span>
              <Combobox
                value={addUserId}
                onChange={setAddUserId}
                placeholder="Select a user..."
                emptyLabel="No more users to add"
                options={availableUsers.map((u) => ({
                  value: u.id,
                  label: u.name ?? u.email ?? u.id,
                  ...(u.email ? { hint: u.email } : {}),
                }))}
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                Roles
              </span>
              <RoleChips value={addRoles} onToggle={(r) => setAddRoles((v) => toggle(v, r))} />
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                GitLab access
              </span>
              <Select
                aria-label="GitLab access"
                value={addAccess}
                onChange={(v) => setAddAccess(v as GitlabAccess | "")}
                options={ACCESS_OPTIONS}
              />
            </div>

            <Button
              type="button"
              size="sm"
              onClick={addMember}
              disabled={!addUserId || addRoles.length === 0 || busy === "add"}
            >
              Add Member
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={members}
        rowKey={(m) => m.id}
        emptyState="No members yet."
      />
    </div>
  );
}
